import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useCreatePriorityRule, useUpdatePriorityRule } from "@/hooks/useReviewPriority";
import { Plus, Save, Trash2 } from "lucide-react";

interface PriorityRule {
  id?: string;
  rule_name: string;
  rule_type: string;
  priority_score: number;
  conditions: any;
  is_active: boolean;
}

export function PriorityRulesManager({ workspaceId }: { workspaceId: string }) {
  const { toast } = useToast();
  const createRule = useCreatePriorityRule();
  const updateRule = useUpdatePriorityRule();

  const [rules, setRules] = useState<PriorityRule[]>([
    {
      rule_name: "High Amount Bills",
      rule_type: "amount_threshold",
      priority_score: 90,
      conditions: { threshold: 10000 },
      is_active: true,
    },
  ]);

  const [editingRule, setEditingRule] = useState<PriorityRule | null>(null);

  const handleSaveRule = async () => {
    if (!editingRule) return;

    try {
      if (editingRule.id) {
        await updateRule.mutateAsync({
          id: editingRule.id,
          updates: editingRule,
        });
      } else {
        await createRule.mutateAsync({
          ...editingRule,
          workspace_id: workspaceId,
        });
      }
      setEditingRule(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save priority rule",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Priority Rules Configuration</CardTitle>
          <CardDescription>
            Define rules to automatically calculate review priority based on bill characteristics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {editingRule ? (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
              <div className="space-y-2">
                <Label>Rule Name</Label>
                <Input
                  value={editingRule.rule_name}
                  onChange={(e) =>
                    setEditingRule({ ...editingRule, rule_name: e.target.value })
                  }
                  placeholder="e.g., High Amount Bills"
                />
              </div>

              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select
                  value={editingRule.rule_type}
                  onValueChange={(value) =>
                    setEditingRule({ ...editingRule, rule_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount_threshold">Amount Threshold</SelectItem>
                    <SelectItem value="sla_breach">SLA Breach Risk</SelectItem>
                    <SelectItem value="low_confidence">Low Confidence</SelectItem>
                    <SelectItem value="anomaly_detected">Anomaly Detected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority Score: {editingRule.priority_score}</Label>
                <Slider
                  value={[editingRule.priority_score]}
                  onValueChange={([value]) =>
                    setEditingRule({ ...editingRule, priority_score: value })
                  }
                  min={0}
                  max={100}
                  step={5}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={editingRule.is_active}
                  onCheckedChange={(checked) =>
                    setEditingRule({ ...editingRule, is_active: checked })
                  }
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveRule} disabled={createRule.isPending || updateRule.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Rule
                </Button>
                <Button variant="outline" onClick={() => setEditingRule(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setEditingRule({ rule_name: "", rule_type: "amount_threshold", priority_score: 50, conditions: {}, is_active: true })}>
              <Plus className="h-4 w-4 mr-2" />
              Add Priority Rule
            </Button>
          )}

          <div className="space-y-2">
            {rules.map((rule, idx) => (
              <Card key={idx}>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="font-medium">{rule.rule_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Priority: {rule.priority_score} • {rule.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingRule(rule)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
